import React, { useEffect, useMemo, useState } from 'react';
import { Info, Loader2, Search } from 'lucide-react';
import { fetchCapabilityGrid, saveCapabilityGrid } from './api';
import type { CapabilityGridData, GridChange, GridScope } from './types';
import { primaryBtnClass, secondaryBtnClass } from '../styles';

interface CapabilityGridProps {
  scope: GridScope;
}

const cellKey = (capId: number, colId: number) => `${capId}:${colId}`;

const CapabilityGrid: React.FC<CapabilityGridProps> = ({ scope }) => {
  const [data, setData] = useState<CapabilityGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [q, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchCapabilityGrid(scope);
      setData(payload);
      setDraft({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load capability grid');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const allowed = (capId: number, colId: number) => {
    const key = cellKey(capId, colId);
    if (key in draft) return draft[key];
    const stored = data?.grants?.[String(capId)]?.[String(colId)];
    if (typeof stored === 'boolean') return stored;
    return data?.default_allowed ?? false;
  };

  const toggle = (capId: number, colId: number) => {
    const next = !allowed(capId, colId);
    const stored = data?.grants?.[String(capId)]?.[String(colId)];
    const original = typeof stored === 'boolean' ? stored : data?.default_allowed ?? false;
    setDraft((prev) => {
      const copy = { ...prev };
      const key = cellKey(capId, colId);
      if (next === original) delete copy[key];
      else copy[key] = next;
      return copy;
    });
  };

  const changes: GridChange[] = useMemo(
    () =>
      Object.entries(draft).map(([key, is_allowed]) => {
        const [cap, col] = key.split(':');
        return {
          capability_id: Number(cap),
          column_id: Number(col),
          is_allowed,
        };
      }),
    [draft]
  );

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!data) return [];
    if (!term) return data.capabilities;
    return data.capabilities.filter(
      (cap) => cap.name.toLowerCase().includes(term) || cap.key.toLowerCase().includes(term)
    );
  }, [data, q]);

  const save = async () => {
    if (!data || changes.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await saveCapabilityGrid(scope, changes);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save capability grid');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading grid…
      </div>
    );
  }

  if (!data) {
    return error ? (
      <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
    ) : null;
  }

  return (
    <div className="pb-16">
      <p className="text-sm text-[#626262] mb-4">
        Tick a cell to grant that capability to a {data.column_label.toLowerCase()}. Hover the
        info icon for a description. Save when you are done.
      </p>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      <div className="relative max-w-sm mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
        <input
          value={q}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter capabilities"
          className="h-10 w-full rounded-md border border-[#D1D1D1] pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
        />
      </div>
      <div className="overflow-auto border border-[#E8E8E8] rounded-lg max-h-[60vh]">
        <table className="text-sm">
          <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
            <tr>
              <th className="sticky left-0 z-10 bg-[#FAFAFA] px-3 py-2.5 font-semibold min-w-[240px]">
                Capability
              </th>
              {data.columns.map((col) => (
                <th key={col.id} className="px-3 py-2.5 font-semibold whitespace-nowrap">
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((cap) => (
              <tr key={cap.id} className="border-t border-[#E8E8E8]">
                <td className="sticky left-0 z-10 bg-white px-3 py-2.5 min-w-[240px]">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#0C0B0B]">{cap.name}</span>
                    {cap.description && (
                      <span className="relative group">
                        <Info
                          className="w-3.5 h-3.5 text-[#8A8A8A]"
                          tabIndex={0}
                          aria-label={cap.description}
                        />
                        <span className="pointer-events-none absolute left-5 top-0 z-20 hidden w-64 rounded-md bg-[#0C0B0B] px-3 py-2 text-[12px] text-white group-hover:block group-focus-within:block">
                          {cap.description}
                        </span>
                      </span>
                    )}
                    {!cap.is_visible && data.can_view_hidden && (
                      <span className="text-[10px] uppercase tracking-wide text-[#8A8A8A]">
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#8A8A8A]">{cap.key}</div>
                </td>
                {data.columns.map((col) => (
                  <td key={col.id} className="px-3 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={allowed(cap.id, col.id)}
                      onChange={() => toggle(cap.id, col.id)}
                      aria-label={`${cap.name} for ${col.name}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={data.columns.length + 1} className="px-3 py-8 text-[#8A8A8A]">
                  No capabilities match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {changes.length > 0 && (
        <div className="sticky bottom-0 mt-4 flex items-center justify-between gap-3 rounded-md border border-[#E8E8E8] bg-white px-4 py-3 shadow-sm">
          <span className="text-sm text-[#4A4A4A]">
            {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={() => setDraft({})} className={secondaryBtnClass}>
              Discard
            </button>
            <button type="button" disabled={saving} onClick={() => void save()} className={primaryBtnClass}>
              {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapabilityGrid;
